import type Luminary from '@lumen5/luminary';

import type { AssetContainerModel } from 'js/src/client';
import { ApiService } from 'js/src/client';
import { ErrorReporter } from 'js/src/libs/error-reporter';

export interface ItemRepresentation {
    id: string;
    type: string;
    title: string;
    description: string;
    tags: string[];
}

export interface ItemEntry<T extends ItemRepresentation> {
    id: string;
    data: AssetContainerModel;
    representation: T;
}

export abstract class BaseRegistry<T extends ItemRepresentation> {
    protected registry: Map<string, ItemEntry<T>> = new Map();

    protected generateId(): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < 6; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        // Ensure uniqueness
        if (this.registry.has(result)) {
            return this.generateId();
        }

        return result;
    }

    // Abstract methods that subclasses must implement
    protected abstract extractItemInfo(data: AssetContainerModel): {
        title: string;
        description: string;
        tags: string[];
    };

    protected abstract validateItemData(data: AssetContainerModel): void;

    protected abstract createRepresentation(
        id: string,
        itemInfo: { title: string; description: string; tags: string[] },
        data: AssetContainerModel,
    ): T;

    protected abstract getItemUuid(data: AssetContainerModel): string | undefined;

    protected abstract isItemAlreadyInRegistry(uuid: string): string | null;

    public abstract addAnyUnseenItemsFromVideo(luminary: Luminary): Promise<void>;

    // Common implementation methods
    public addItem(data: AssetContainerModel): string {
        this.validateItemData(data);

        const uuid = this.getItemUuid(data);
        if (!uuid) {
            throw new Error('Invalid item data: must contain a UUID');
        }

        // Check if item already exists
        const existingId = this.isItemAlreadyInRegistry(uuid);
        if (existingId) {
            return existingId;
        }

        // Create a new item entry
        const id = this.generateId();
        const itemInfo = this.extractItemInfo(data);
        const representation = this.createRepresentation(id, itemInfo, data);

        const entry: ItemEntry<T> = {
            id,
            data,
            representation,
        };

        this.registry.set(id, entry);
        return id;
    }

    public getItem(id: string): AssetContainerModel | null {
        const entry = this.registry.get(id);
        return entry ? entry.data : null;
    }

    public getRepresentation(id: string): T | null {
        const entry = this.registry.get(id);
        return entry ? entry.representation : null;
    }

    public hasItem(id: string): boolean {
        return this.registry.has(id);
    }

    public getAllRepresentations(): T[] {
        return Array.from(this.registry.values()).map((entry) => entry.representation);
    }

    public clear(): void {
        this.registry.clear();
    }

    public getItemIdByUuid(uuid: string): string | null {
        for (const [id, entry] of this.registry.entries()) {
            if (this.getItemUuid(entry.data) === uuid) {
                return id;
            }
        }
        return null;
    }

    public async addItemByUuid(uuid: string): Promise<string | null> {
        if (this.isItemAlreadyInRegistry(uuid)) {
            return this.getItemIdByUuid(uuid);
        }

        // fetch item data from API and add it
        await this.fetchItemsFromApi(new Set([uuid])).then((items) => {
            items.forEach((item) => this.addItem(item));
        });

        return this.getItemIdByUuid(uuid);
    }

    protected async fetchItemsFromApi(unseenUuids: Set<string>): Promise<AssetContainerModel[]> {
        if (unseenUuids.size === 0) {
            return [];
        }

        const uuidsString = Array.from(unseenUuids).join(',');
        try {
            const response = await ApiService.bulkMediaAssetsList({
                query: {
                    uuids: uuidsString,
                },
            });

            return response.data || [];
        } catch (error) {
            ErrorReporter.capture(error);
            return [];
        }
    }
}
